import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

type Status = 'pending' | 'partial' | 'paid' | 'overpaid' | 'waived';

interface Invoice {
  id: string;
  leaseId: string;
  unitId: string;
  tenantId: string;
  periodYear: number;
  periodMonth: number;
  dueDate: string;
  amountExpected: number;
  amountReceived: number;
  status: Status;
}

interface Tenant { id: string; fullName: string; }
interface Unit   { id: string; kind: string; flatNo: string | null; roomNo: string | null; }

export function RentPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [status, setStatus] = useState<'' | Status>('');
  const qc = useQueryClient();

  const invoicesQ = useQuery({
    queryKey: ['rent', 'invoices', year, month, status],
    queryFn: () =>
      api<{ invoices: Invoice[] }>(
        `/api/v1/rent/invoices?year=${year}&month=${month}${status ? `&status=${status}` : ''}`,
      ),
  });
  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ tenants: Tenant[] }>('/api/v1/tenants'),
  });
  // Units come from the property listing; we fetch on demand per row via a
  // simple lookup map built from the tenant page later. For now, tenant ids
  // are the practical human anchor.
  const tenantLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenantsQ.data?.tenants ?? []) m.set(t.id, t.fullName);
    return m;
  }, [tenantsQ.data]);

  const totals = useMemo(() => {
    const rows = invoicesQ.data?.invoices ?? [];
    const expected = rows.reduce((a, r) => a + r.amountExpected, 0);
    const received = rows.reduce((a, r) => a + r.amountReceived, 0);
    return { expected, received, pending: Math.max(0, expected - received), count: rows.length };
  }, [invoicesQ.data]);

  const generate = useMutation({
    mutationFn: (body: { year: number; month: number }) =>
      api('/api/v1/rent/generate', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rent'] }),
  });

  return (
    <Page
      title="Rent"
      actions={
        <Button
          variant="secondary"
          disabled={generate.isPending}
          onClick={() => generate.mutate({ year, month })}
        >
          {generate.isPending ? 'Generating…' : 'Generate for this month'}
        </Button>
      }
    >
      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-4 md:items-end">
          <Field label="Year">
            <TextInput
              type="number" value={year} min={2000} max={2100}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </Field>
          <Field label="Month">
            <select
              value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
            >
              {monthNames.map((n, i) => (<option key={n} value={i + 1}>{n}</option>))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status} onChange={(e) => setStatus(e.target.value as '' | Status)}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
            >
              <option value="">Any</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="overpaid">Overpaid</option>
              <option value="waived">Waived</option>
            </select>
          </Field>
          <div className="text-right text-sm">
            <div className="text-slate-500">Expected</div>
            <div className="text-lg font-medium">{Money.fromPaise(totals.expected).format()}</div>
            <div className="text-slate-500 mt-1">Received / Pending</div>
            <div>
              {Money.fromPaise(totals.received).format()}
              {' · '}
              <span className={totals.pending > 0 ? 'text-amber-700 dark:text-amber-400' : ''}>
                {Money.fromPaise(totals.pending).format()}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {invoicesQ.isLoading ? (
        <Empty>Loading…</Empty>
      ) : !invoicesQ.data?.invoices.length ? (
        <Empty>
          No invoices for {monthNames[month - 1]} {year}. Try{' '}
          <button className="underline" onClick={() => generate.mutate({ year, month })}>generate</button>
          {' '}or pick another month.
        </Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b px-3 py-2 dark:border-slate-700">Status</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Tenant</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Due</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Expected</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Received</th>
              <th className="border-b px-3 py-2 dark:border-slate-700 text-right">Pending</th>
              <th className="border-b px-3 py-2 dark:border-slate-700"></th>
            </tr>
          </thead>
          <tbody>
            {invoicesQ.data.invoices.map((inv) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                tenantName={tenantLookup.get(inv.tenantId) ?? inv.tenantId.slice(-6)}
              />
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function InvoiceRow({ invoice, tenantName }: { invoice: Invoice; tenantName: string }) {
  const [open, setOpen] = useState(false);
  const pending = Math.max(0, invoice.amountExpected - invoice.amountReceived);
  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-800">
        <td className="px-3 py-2"><StatusBadge status={invoice.status} /></td>
        <td className="px-3 py-2">{tenantName}</td>
        <td className="px-3 py-2 text-slate-500">{invoice.dueDate}</td>
        <td className="px-3 py-2 text-right">{Money.fromPaise(invoice.amountExpected).format()}</td>
        <td className="px-3 py-2 text-right">{Money.fromPaise(invoice.amountReceived).format()}</td>
        <td className="px-3 py-2 text-right">
          <span className={pending > 0 ? 'text-amber-700 dark:text-amber-400' : ''}>
            {Money.fromPaise(pending).format()}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          <Button variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : invoice.status === 'paid' || invoice.status === 'waived' ? 'View' : 'Record payment'}
          </Button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-slate-50 px-3 py-4 dark:bg-slate-900">
            <PaymentForm invoiceId={invoice.id} suggestedAmount={pending} disabled={invoice.status === 'waived'} />
          </td>
        </tr>
      )}
    </>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const tone = status === 'paid' ? 'good'
    : status === 'overpaid' ? 'good'
    : status === 'partial' ? 'warn'
    : status === 'waived' ? 'neutral'
    : 'bad';
  return <Badge tone={tone}>{status}</Badge>;
}

function PaymentForm({
  invoiceId, suggestedAmount, disabled,
}: { invoiceId: string; suggestedAmount: number; disabled: boolean }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(
    suggestedAmount > 0 ? Money.fromPaise(suggestedAmount).toRupees().toString() : '',
  );
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<'cash' | 'bank' | 'upi' | 'cheque' | 'other'>('upi');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/v1/rent/invoices/${invoiceId}/payments`, { method: 'POST', body }),
    onSuccess: () => {
      setReference('');
      void qc.invalidateQueries({ queryKey: ['rent'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = Money.parse(amount).toPaise();
      mutation.mutate({
        amount: paise,
        paidOn,
        method,
        reference: reference || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  if (disabled) return <p className="text-sm text-slate-500">Waived — no payments accepted.</p>;

  return (
    <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-5 md:items-end">
      <Field label="Amount">
        <TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label="Paid on">
        <TextInput type="date" required value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>
      <Field label="Method">
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value as typeof method)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        >
          <option value="cash">Cash</option>
          <option value="bank">Bank</option>
          <option value="upi">UPI</option>
          <option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label="Reference">
        <TextInput value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI ref / txn id" />
      </Field>
      <div className="flex items-center justify-end gap-2">
        {error && <span className="text-xs text-red-600">{error}</span>}
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Record'}
        </Button>
      </div>
    </form>
  );
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
